import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import {
    ReferralBetsExtendedType,
    ReferralBetsLegsExtendedType,
} from 'src/api/config/betting/getReferredBetSlipDecode';
import { ReferralParsedLegsType } from 'src/domains/sportsbook/betting/betting/getReferredTypes';
import {
    ParsedLegsType,
    ParsedPossibleBetsType,
    ParsedCombinationsType,
    CombinationRawType,
    ErrorRawType,
    BetResponseType,
    RabRawType,
    SelectedBetsInfoType,
} from './getPossibleBetsTypes';

export const onCheckIsRelated = (combinations: Record<string, ParsedCombinationsType>): boolean => {
    return Object.values(combinations).some((combination) => {
        if (combination.errors !== undefined) {
            return combination.errors.some((err) => err.resource === 'Selection' && err.code === 'related');
        }
        return false;
    });
};

export const onCheckIsSinglesOnly = (combinations: Record<string, ParsedCombinationsType>): boolean => {
    return Object.values(combinations).some((combination) => {
        if (combination.errors !== undefined) {
            return combination.errors.some((err) => err.resource === 'Market' && err.code === 'singles-only');
        }
        return false;
    });
};

export function convertBetsToLegs(
    bets: Array<BetResponseType> /*, combinations: Record<string, ParsedCombinationsType>*/
): Record<string, ParsedLegsType> {
    const result: Record<string, ParsedLegsType> = {};

    for (const bet of bets) {
        if (bet.id.substr(0, 3) === 'all') {
            continue;
        }

        const firstLeg = bet.legs[0];
        if (firstLeg !== undefined && firstLeg.selection !== undefined) {
            result[firstLeg.selection.id] = {
                eachWay: bet.eachWay,
                potentialReturns: bet.potentialReturns ?? null,
                potentialReturnsAt: bet.stakePerLine,
                freebetCredits: bet.freebetCredits,
                freebetRemarks: bet.freebetRemarks,
                freeBets: bet.freeBets,
                maxStake: bet.maxStake ?? null,
                price:
                    bet.type === 'SGL' && bet.legs.length === 1 && firstLeg.price !== undefined
                        ? firstLeg.price
                        : bet.price,
                errors: bet.errors,
            };
        }
    }

    return result;
}

export function convertBetsToCombinations(
    bets: Record<string, CombinationRawType>,
    legs: Array<BetResponseType>
): Record<string, ParsedCombinationsType> {
    const result: Record<string, ParsedCombinationsType> = {};

    for (const bet of Object.values(bets)) {
        const betFromLegs = legs.find((leg) => leg.type === bet.type);

        if (bet.type === 'SGL') {
            continue;
        }
        const errors =
            betFromLegs === undefined
                ? bet.errors?.filter((err) => !(err.code === 'suspended' && err.resource === 'Selection'))
                : betFromLegs.errors;

        result[bet.type] = {
            type: bet.type,
            ewOffered: bet.ewOffered,
            name: bet.name,
            potentialReturns: bet.potentialReturns ?? null,
            potentialReturnsEw: bet.potentialReturnsEw ?? null,
            freebetCredits: bet.freebetCredits ?? undefined,
            freebetRemarks: bet.freebetRemarks ?? undefined,
            freeBets: bet.freeBets ?? undefined,
            maxStake: bet.maxStake ?? undefined,
            potentialReturnsAt: bet.stakePerLine,
            numLines: bet.numLines,
            price: bet.price ?? undefined,
            errors: errors ?? undefined,
            legs: bet.legs ?? [],
        };
    }

    return result;
}

export const onCheckIsRelatedOnAdd = (combinations: Record<string, ParsedCombinationsType>): boolean => {
    let relatedOnAdd = false;
    for (const bet of Object.values(combinations)) {
        if (!relatedOnAdd) {
            const betMarketsIds: Array<number> = [];

            if (bet.legs.length > 0) {
                if (bet.type === 'SGL') {
                    continue;
                }

                for (const leg of bet.legs) {
                    if (leg.market !== undefined) {
                        // eslint-disable-next-line no-negated-condition
                        if (!betMarketsIds.includes(leg.market.id)) {
                            betMarketsIds.push(leg.market.id);
                        } else {
                            relatedOnAdd = true;
                        }
                    }
                }
            }
        }
    }
    return relatedOnAdd;
};

export function convertBets(
    bets: Array<BetResponseType>,
    combinations: Record<string, CombinationRawType>,
    rabBets: Array<RabRawType>,
    multibetBets: Array<{ correlationId: string; freeBets?: Array<string> | null }> | null | undefined,
    errors: Array<ErrorRawType>,
    stakeOneCombinations: Record<string, CombinationRawType>,
    selectedBetsInfo?: SelectedBetsInfoType
): ParsedPossibleBetsType {
    for (const bet of bets) {
        const combination = combinations[bet.type];

        if (bet.legs.length > 1 && combination !== undefined) {
            combination.stakePerLine = bet.stakePerLine;
            combination.price = bet.price;
            combination.freebetCredits = bet.freebetCredits;
            combination.freebetRemarks = bet.freebetRemarks;
            combination.freeBets = bet.freeBets;
            combination.maxStake = bet.maxStake;
        }
    }

    const convertedCombinations = convertBetsToCombinations(combinations, bets);
    // TODO - only for BetslipSpecialMessageComponent - to refactor
    const convertedStakeOneCombinations = convertBetsToCombinations(stakeOneCombinations, bets);
    const relatedOnAdd = onCheckIsRelatedOnAdd(convertedCombinations);
    const convertedLegs = convertBetsToLegs(bets); //, convertedCombinations);
    const isRelated = onCheckIsRelated(convertedCombinations); // Object.values(convertedLegs).some(leg => leg.related);
    const isSinglesOnly = onCheckIsSinglesOnly(convertedCombinations); //Object.values(convertedLegs).some(leg => leg['singles-only']);

    let singleError: ErrorRawType | null = null;

    for (const err of errors) {
        if (err.field === 'playableBalance' && err.code === 'minimum' && err.resource === 'Wallet') {
            singleError = err;
            break;
        }
    }

    return {
        combinations: convertedCombinations,
        stakeOneCombinations: convertedStakeOneCombinations,
        legs: convertedLegs,
        rabBets,
        multibetBets: (multibetBets ?? []).map((bet) => ({
            correlationId: bet.correlationId,
            freeBets: bet.freeBets ?? null,
        })),
        related: isRelated,
        singlesOnly: isSinglesOnly,
        playableBalanceAmounts: singleError === null ? null : singleError.details,
        bets,
        relatedOnAdd,
        selectedBetsInfo,
        errors,
    };
}

const parseLeg = (leg: ReferralBetsLegsExtendedType): ReferralParsedLegsType => {
    const legInner: ReferralParsedLegsType = {
        type: leg.type,
        price: leg.price,
        priceType: leg.priceType,
        index: '-',
        stakePerLine: '0',
        eachWay: false,
        empty: false,
        selectionName: undefined,
        eventName: undefined,
        id: null,
        eventId: null,
        marketId: null,
        marketName: null,
        selectionId: null,
        betEventId: null,
        betEventName: null,
        potentialReturns: null,
        isBB: undefined,
        legReference: undefined,
        internalEventId: undefined,
        bbName: undefined,
        provider: undefined,
    };

    if (leg.type === 'betBuilder' || leg.legReference !== undefined) {
        legInner.isBB = true;
        legInner.legReference = leg.legReference ?? undefined;
        legInner.internalEventId = leg.internalEventId ?? undefined;
        legInner.bbName = leg.name ?? undefined;
        legInner.provider = leg.provider ?? undefined;
        legInner.empty = false;
        legInner.id = `bb-${leg.legReference?.geniusMarketId ?? 'unknown'}-${leg.legReference?.geniusFixtureId ?? 'unknown'}`;
        legInner.price = leg.price;
        legInner.priceType = leg.priceType;
        return legInner;
    }

    let id: number | null = null;
    const selectionId = leg.selectionId ?? null;
    const eventId = leg.eventId ?? null;
    const marketId = leg.marketId ?? null;
    const event = leg.event;
    const market = leg.market;

    if (selectionId === null) {
        legInner.empty = true;
        legInner.selectionName = event?.name ?? leg.selection?.name ?? undefined;
        legInner.eventName = event?.name ?? undefined;
    } else {
        id = selectionId;

        legInner.eventId = eventId ?? event?.id ?? null;
        legInner.marketId = marketId ?? market?.id ?? null;
        legInner.selectionId = selectionId;
    }

    legInner.id = id;
    legInner.eventId = eventId;
    legInner.marketId = marketId;
    legInner.selectionId = selectionId;

    return legInner;
};

export const mapOfferBets = (
    bets: Array<ReferralBetsExtendedType>,
    expiresAt: string | null | undefined,
    type: string,
    configComponents: ConfigComponents
): unknown => {
    const combinations: Record<string, ReferralBetsExtendedType> = {};
    const legs: Record<string, ReferralParsedLegsType> = {};
    let channel;
    let emptyBetId = 0;

    for (const bet of bets) {
        if (bet.type === 'BB' && bet.legs.length >= 1) {
            const firstLeg = bet.legs[0] ?? null;
            const leg = firstLeg === null ? null : parseLeg(firstLeg);
            if (leg !== null && leg.id !== null) {
                legs[leg.id] = leg;
                leg.index = leg.id;
                leg.stakePerLine = configComponents.precision.newFromAnything(bet.stakePerLine).value;
                leg.eachWay = bet.eachWay;
                leg.potentialReturns =
                    bet.potentialReturns === null
                        ? null
                        : configComponents.precision.newFromAnything(bet.potentialReturns).value;
            }
            channel = bet.channel;
            continue;
        }

        if (bet.type === 'SGL' && bet.legs.length === 1) {
            const firstLeg = bet.legs[0] ?? null;
            const leg = firstLeg === null ? null : parseLeg(firstLeg);
            let id = leg === null ? null : leg.id;
            if (leg !== null && id !== null) {
                const singleLeg = legs[id.toString()] ?? null;

                if (leg.empty !== undefined && leg.empty !== false) {
                    emptyBetId++;
                    id = `empty${emptyBetId}`;
                } else if (singleLeg !== null) {
                    const tempId = `${id}${Object.keys(legs).length}`;

                    id = parseInt(tempId, 10);
                }

                legs[id] = leg;
                leg.index = id;
                leg.stakePerLine = configComponents.precision.newFromAnything(bet.stakePerLine).value;
                leg.eachWay = bet.eachWay;
                leg.potentialReturns =
                    bet.potentialReturns === null
                        ? null
                        : configComponents.precision.newFromAnything(bet.potentialReturns).value;
            }
        } else {
            // @TODO: Think about multiple combinations of same type
            // @TODO: Think about combinations of part legs

            combinations[bet.type] = bet;

            for (const legInner of bet.legs) {
                const leg = parseLeg(legInner);
                let id = leg.id;
                if (id !== null) {
                    const singleLeg = legs[id.toString()] ?? null;

                    if (leg.empty !== undefined && leg.empty !== false) {
                        emptyBetId++;
                        id = `empty${emptyBetId}`;
                    } else if (singleLeg !== null) {
                        continue;
                    }

                    legs[id] = leg;

                    leg.index = id;
                    leg.stakePerLine = '0';
                    leg.eachWay = false;
                }
            }
        }
        channel = bet.channel;
    }

    return { combinations, legs, channel, expiresAt, type };
};
